import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Loading from '../../../../components/Shared/Loader/Loading';
import NtxTable from '../../../../components/DataTable/NtxTable';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';

import {
  getPeriodSourceByPeriodId,
  getInvPeriodSourceByPeriodId,
  getPurPeriodSourceByPeriodId,
  getApPeriodSourceByPeriodId,
  getArPeriodSourceByPeriodId,
  getFleetPeriodSourceByPeriodId,
  getFaPeriodSourceByPeriodId,
  UpdateInvPeriodSource,
  UpdateApPeriodSource,
  UpdateFleetPeriodSource,
  UpdateArPeriodSource,
  UpdatePurPeriodSource,
  UpdatePeriodSource,
  UpdateFaPeriodSource
} from '../../../../store/Finance/Periods/PeriodsSlice';

import CustomAlert from '../../../../components/Shared/Alert/CustomAlert';
import {type  PeriodSourceModels } from '../../../../store/Finance/Periods/PeriodsModels';
import confirmAction from '../../../../components/Shared/Alert/confirmAction';

interface Props {
  customProp?: 'Inv' | 'Ap' | 'Ar' | 'Fleet' | 'Su' | 'Pur' | 'FA';
  periodId: string;
}

const ClosingAccountingPeriod = ({ periodId, customProp }: Props) => {
  const dispatch = useAppDispatch();
  // console.log(customProp);
  const { t, i18n } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (customProp === 'Inv') {
      dispatch(getInvPeriodSourceByPeriodId(periodId));
    } else if (customProp === 'Pur') {
      dispatch(getPurPeriodSourceByPeriodId(periodId));
    } else if (customProp === 'Ap') {
      dispatch(getApPeriodSourceByPeriodId(periodId));
    } else if (customProp === 'Ar') {
      dispatch(getArPeriodSourceByPeriodId(periodId));
    } else if (customProp === 'FA') {
      dispatch(getFaPeriodSourceByPeriodId(periodId));
    } else if (customProp === 'Fleet') {
      dispatch(getFleetPeriodSourceByPeriodId(periodId));
    } else {
      dispatch(getPeriodSourceByPeriodId(periodId));
    }
  }, [customProp, dispatch, periodId]);
  const { PeriodSourceList, loading2, error2 } = useAppSelector(state => state.Periods);

  const columns = [
    { title: t('#'), key: 'num', className: 'text-theme fw-bold fs-5 w-25' },
    { title: t('Source'), key: 'Name', className: 'w-25' },
    { title: t('Status'), key: 'status', className: 'w-25' },
    {
      title: isSubmitting && <div className='spinner-border spinner-border-sm text-theme'></div>,
      key: 'action',
      className: 'text-center w-25'
    }
  ];

  const tableData = PeriodSourceList?.slice()
    ?.sort((a, b) => a.sequance - b.sequance)
    ?.map(item => {
      let rowClass;

      return {
        num: item.sequance,
        Name: i18n.language === 'en' ? item.sourceName : item.sourceName2,
        status: (
          <div className='align-middle'>
            <span className={`badge ${item.activeFlag === true ? 'bg-success' : 'bg-danger'}`}>
              {item.activeFlag === true ? 'مفتوحة' : 'مغلقة'}
            </span>
          </div>
        ),
        action: (
          <button
            disabled={isSubmitting}
            type='button'
            className={`btn btn-sm w-100 ${!item.activeFlag ? 'btn-success' : 'btn-danger'}`}
            onClick={() => handleCloseingPeriod(item)}
          >
            {false ? (
              <div className='spinner-border spinner-border-sm me-2'></div>
            ) : (
              <i
                className={`fas fa-lg fa-fw me-2 ${!item.activeFlag ? 'fa-unlock' : 'fa-lock'}`}
              ></i>
            )}
            {t(!item.activeFlag ? 'فتح' : 'إغلاق')}
          </button>
        ),

        rowClass // استخدم rowClass هنا
      };
    });

  //~~

  const handleCloseingPeriod = async (item: PeriodSourceModels) => {
    try {
      const isConfirmation = await confirmAction(
        `هل أنت متأكد من ${!item.activeFlag ? 'إغلاق' : 'فتح'} هذه الفترة؟`
      );
      if (!isConfirmation) return;
      setIsSubmitting(true); // تعيين حالة التنفيذ إلى true

      let body = {
        periodId: item.periodId,
        sourceId: item.sourceId,
        activeFlag: !item.activeFlag // إرسال القيمة العكسية
      };

      let action;
      switch (customProp) {
        case 'Inv':
          action = UpdateInvPeriodSource({
            PeriodId: body.periodId,
            ActiveFlag: body.activeFlag
          });
          break;
        case 'Ap':
          action = UpdateApPeriodSource({
            PeriodId: body.periodId,
            ActiveFlag: body.activeFlag
          });
          break;
        case 'Ar':
          action = UpdateArPeriodSource({
            PeriodId: body.periodId,
            ActiveFlag: body.activeFlag
          });
          break;
        case 'FA':
          action = UpdateFaPeriodSource({
            PeriodId: body.periodId,
            ActiveFlag: body.activeFlag
          });
          break;
        case 'Pur':
          action = UpdatePurPeriodSource({
            PeriodId: body.periodId,
            ActiveFlag: body.activeFlag
          });
          break;
        case 'Fleet':
          action = UpdateFleetPeriodSource({
            PeriodId: body.periodId,
            ActiveFlag: body.activeFlag
          });
          break;
        default:
          action = UpdatePeriodSource(body);
      }

      // const res = await dispatch(UpdatePeriodSource(body)).unwrap();
      const res = await dispatch(action).unwrap();

      if (res?.succeeded === true && res.data === true) {
        CustomAlert({ action: 'Add', msg: 'تمت العملية بنجاح' });
      } else {
        CustomAlert({ action: 'Error' });
      }
    } catch (error) {
      CustomAlert({ action: 'Error' });
      console.error('Error closing period:', error);
    } finally {
      setIsSubmitting(false); // إعادة تعيين الحالة إلى false عند الانتهاء
    }
  };

  return (
    <div>
      <Loading loading={loading2} error={error2} Type='Dots'>
        <NtxTable
          data={tableData || []}
          columns={columns}
          dataPerPage={6}
          theadClass='bg-theme bg-opacity-10'
          tableClass='table text-nowrap table-hover '
          enableSearch
          // showRecordsSelector
        />
      </Loading>
    </div>
  );
};

export default ClosingAccountingPeriod;
